import {
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
} from '@tanstack/react-query';
import {
  MsLevel,
  SampleIonPolarity,
  SampleOrderField,
  SampleStatus,
} from '../../enums';
import { ChatAgentInfo } from '../../interfaces/chat';
import { ChangeLogResponse } from '../../interfaces/compound.db';
import {
  AgentJobStatus,
  Config,
  Job,
  Model,
  Product,
  ProductViewer,
  SpectraResponse,
} from '../../interfaces/job';
import { InterpretiveReport } from '../../interfaces/interpretiveReport';
import { PaginatedResponse } from '../../interfaces/misc';
import { Organization } from '../../interfaces/organization';
import { Sample, SampleSet, TicData } from '../../interfaces/sampleset';
import { LegalAcceptanceStatusResponse, UserData } from '../../interfaces/user';
import {
  PermissionString,
  READ_USER_AGENT_CHAT,
} from '../../utils/permissions';
import { CompoundDBClient } from '../compound.db';
import { NovaApiClient } from '../nova';

export const legalAcceptanceStatusQueryOptions = (
  hasPermission: (permission: PermissionString) => boolean,
  enabled: boolean
) =>
  queryOptions<LegalAcceptanceStatusResponse>({
    queryKey: ['legalAcceptanceStatus'] as const,
    queryFn: async () => {
      const novaApi = new NovaApiClient(hasPermission);
      return await novaApi.checkLegalAcceptanceStatus();
    },
    enabled,
    refetchInterval: 5 * 60 * 1000, // 5 minutes
  });

export const analyteHistoryInfiniteQueryOptions = (
  hasPermission: (permission: PermissionString) => boolean,
  analyteId: string,
  batchSize = 20
) =>
  infiniteQueryOptions({
    queryKey: ['analyteHistory', analyteId] as const,
    queryFn: async ({ pageParam }): Promise<ChangeLogResponse> => {
      const compoundDb = new CompoundDBClient(hasPermission);
      return await compoundDb.getAnalyteHistory(
        analyteId,
        pageParam,
        batchSize
      );
    },
    initialPageParam: 0,
    getNextPageParam: (
      lastPage: ChangeLogResponse,
      allPages: ChangeLogResponse[]
    ) => {
      const totalLoaded = allPages.reduce(
        (sum, page) => sum + page.diffs.length,
        0
      );
      if (totalLoaded >= lastPage.total) {
        return undefined;
      }
      return totalLoaded;
    },
    enabled: !!analyteId,
  });

export const sampleSetsInfiniteQueryOptions = (
  hasPermission: (permission: PermissionString) => boolean,
  searchQuery: string,
  showUserOwnedOnly: boolean = false,
  batchSize = 20
) =>
  infiniteQueryOptions({
    queryKey: ['sampleSets', searchQuery, showUserOwnedOnly] as const,
    queryFn: async ({ pageParam }): Promise<PaginatedResponse<SampleSet>> => {
      const novaApi = new NovaApiClient(hasPermission);
      return await novaApi.getSampleSets(
        pageParam,
        batchSize,
        searchQuery || undefined,
        showUserOwnedOnly
      );
    },
    initialPageParam: 0,
    getNextPageParam: (
      lastPage: PaginatedResponse<SampleSet>,
      allPages: PaginatedResponse<SampleSet>[]
    ) => {
      const totalLoaded = allPages.reduce(
        (sum, page) => sum + page.data.length,
        0
      );
      if (totalLoaded >= lastPage.total) {
        return undefined;
      }
      return totalLoaded;
    },
  });

export const sampleSetQueryOptions = (
  sampleSetId: string,
  hasPermission: (permission: PermissionString) => boolean
) =>
  queryOptions({
    queryKey: ['sampleSet', sampleSetId] as const,
    queryFn: async () => {
      const novaApi = new NovaApiClient(hasPermission);
      return await novaApi.getSampleSetById(sampleSetId);
    },
    enabled: !!sampleSetId,
    retry: 1,
  });

export const interpretiveReportQueryOptions = (
  reportId: string,
  hasPermission: (permission: PermissionString) => boolean
) =>
  queryOptions<InterpretiveReport>({
    queryKey: ['interpretiveReport', reportId] as const,
    queryFn: async () => {
      const novaApi = new NovaApiClient(hasPermission);
      return await novaApi.getInterpretiveReport(reportId);
    },
    enabled: !!reportId,
    retry: 1,
  });

/**
 * The report's built HTML artifact (fetched via a presigned URL). Kept separate
 * from the record query so the viewer can show metadata while the (larger)
 * artifact loads. The artifact is immutable per version, so it never refetches.
 */
export const interpretiveReportArtifactQueryOptions = (
  reportId: string,
  hasPermission: (permission: PermissionString) => boolean
) =>
  queryOptions<string>({
    queryKey: ['interpretiveReportArtifact', reportId] as const,
    queryFn: async () => {
      const novaApi = new NovaApiClient(hasPermission);
      return await novaApi.getActiveReportArtifactHtml(reportId);
    },
    enabled: !!reportId,
    retry: 1,
    staleTime: Infinity,
  });

/**
 * Interpretive reports for a sample set. Drives the conditional Reports tab
 * (shown only when total > 0), the default-landing redirect, and the tab
 * content — all share this cache entry.
 */
export const sampleSetInterpretiveReportsQueryOptions = (
  sampleSetId: string,
  hasPermission: (permission: PermissionString) => boolean
) =>
  queryOptions({
    queryKey: ['sampleSetInterpretiveReports', sampleSetId] as const,
    queryFn: async () => {
      const novaApi = new NovaApiClient(hasPermission);
      return await novaApi.getSampleSetInterpretiveReports(sampleSetId);
    },
    enabled: !!sampleSetId,
  });

/**
 * The chat-agent allowlist. Static per environment, so it caches for a while.
 * Gated on the chat feature scope: without it the query stays disabled and no
 * (would-be 403) request is made.
 */
export const chatAgentsQueryOptions = (
  hasPermission: (permission: PermissionString) => boolean
) =>
  queryOptions<ChatAgentInfo[]>({
    queryKey: ['chatAgents'] as const,
    queryFn: async () => {
      const novaApi = new NovaApiClient(hasPermission);
      return await novaApi.getChatAgents();
    },
    enabled: hasPermission(READ_USER_AGENT_CHAT),
    staleTime: 5 * 60 * 1000,
  });

export const jobsQueryOptions = (
  sampleSetId: string,
  hasPermission: (permission: PermissionString) => boolean
) =>
  queryOptions({
    queryKey: ['jobs', sampleSetId] as const,
    queryFn: async () => {
      const novaApi = new NovaApiClient(hasPermission);
      const response = await novaApi.getJobs(0, 40, sampleSetId);

      // Sort all jobs by created_date descending (newest first)
      const allJobs = response.data.sort(
        (a, b) =>
          new Date(b.created_date).getTime() -
          new Date(a.created_date).getTime()
      );

      // Filter for completed jobs
      const completedJobs = allJobs.filter(
        (job) => job.job_status === 'REPORTED'
      );

      return {
        allJobs,
        completedJobs,
      };
    },
    refetchInterval: 30000, // Poll every 30 seconds
    enabled: !!sampleSetId,
  });

export const jobByIdQueryOptions = (
  jobId: string | null,
  hasPermission: (permission: PermissionString) => boolean
) =>
  queryOptions<Job>({
    queryKey: ['job', jobId] as const,
    queryFn: async () => {
      const novaApi = new NovaApiClient(hasPermission);
      return await novaApi.getJobById(jobId as string);
    },
    enabled: !!jobId,
  });

export const samplesQueryOptions = (
  sampleSetId: string,
  hasPermission: (permission: PermissionString) => boolean
) =>
  queryOptions({
    queryKey: ['samples', sampleSetId] as const,
    queryFn: async () => {
      const novaApi = new NovaApiClient(hasPermission);
      return await novaApi.getSamplesBySampleSetId(sampleSetId, false, false);
    },
    refetchInterval: 30000, // Poll every 30 seconds
    enabled: !!sampleSetId,
  });

export const sampleMismatchesQueryOptions = (
  sampleSetId: string,
  hasPermission: (permission: PermissionString) => boolean
) =>
  queryOptions<Record<string, Record<string, string>>>({
    queryKey: ['sampleMismatches', sampleSetId] as const,
    queryFn: async () => {
      const novaApi = new NovaApiClient(hasPermission);
      return await novaApi.getSampleMismatches(sampleSetId);
    },
    enabled: !!sampleSetId,
  });

export const ticForSampleQueryOptions = (
  sampleId: string,
  hasPermission: (permission: PermissionString) => boolean
) =>
  queryOptions<TicData>({
    queryKey: ['ticForSample', sampleId] as const,
    queryFn: async () => {
      const novaApi = new NovaApiClient(hasPermission);
      return await novaApi.getTicForSample(sampleId);
    },
    enabled: !!sampleId,
    retry: false,
  });

export interface PaginatedSamplesParams {
  sampleSetId: string;
  page: number;
  rowsPerPage: number;
  searchQuery: string;
  statusFilter: string[];
  modeFilter: SampleIonPolarity[];
  msLevelFilter?: MsLevel[];
  orderBy: SampleOrderField;
  orderDirection: 'asc' | 'desc';
  metadataFilters?: Record<string, string[]>;
  createdFrom?: string | null;
  createdTo?: string | null;
}

export const paginatedSamplesQueryOptions = (
  hasPermission: (permission: PermissionString) => boolean,
  params: PaginatedSamplesParams
) =>
  queryOptions<PaginatedResponse<Sample>>({
    queryKey: [
      'paginatedSamples',
      params.sampleSetId,
      params.page,
      params.rowsPerPage,
      params.searchQuery,
      params.statusFilter,
      params.modeFilter,
      params.msLevelFilter ?? [],
      params.orderBy,
      params.orderDirection,
      params.metadataFilters,
      params.createdFrom ?? null,
      params.createdTo ?? null,
    ] as const,
    queryFn: async () => {
      const novaApi = new NovaApiClient(hasPermission);
      return await novaApi.getPaginatedSamplesBySampleSet(
        params.sampleSetId,
        (params.page - 1) * params.rowsPerPage,
        params.rowsPerPage,
        params.searchQuery || undefined,
        params.statusFilter.length > 0
          ? params.statusFilter.map(
              (s: string) => SampleStatus[s as SampleStatus]
            )
          : undefined,
        params.orderBy,
        params.orderDirection === 'desc',
        params.modeFilter.length > 0 ? params.modeFilter : undefined,
        params.msLevelFilter && params.msLevelFilter.length > 0
          ? params.msLevelFilter
          : undefined,
        params.metadataFilters,
        params.createdFrom ?? undefined,
        params.createdTo ?? undefined
      );
    },
    refetchInterval: 15000,
    placeholderData: keepPreviousData,
    enabled: !!params.sampleSetId,
  });

export interface FilteredSampleIdsParams {
  sampleSetId: string;
  searchQuery: string;
  statusFilter: string[];
  modeFilter: SampleIonPolarity[];
  msLevelFilter: MsLevel[];
  metadataFilters: Record<string, string[]>;
  createdFrom: string | null;
  createdTo: string | null;
}

const FILTERED_SAMPLE_IDS_PAGE_SIZE = 500;

export const filteredSampleIdsQueryOptions = (
  hasPermission: (permission: PermissionString) => boolean,
  params: FilteredSampleIdsParams
) =>
  queryOptions<{ ids: string[] }>({
    queryKey: [
      'filteredSampleIds',
      params.sampleSetId,
      params.searchQuery,
      params.statusFilter,
      params.modeFilter,
      params.msLevelFilter,
      params.metadataFilters,
      params.createdFrom ?? null,
      params.createdTo ?? null,
    ] as const,
    queryFn: async () => {
      const novaApi = new NovaApiClient(hasPermission);
      const status =
        params.statusFilter.length > 0
          ? params.statusFilter.map(
              (s: string) => SampleStatus[s as SampleStatus]
            )
          : undefined;
      const polarity =
        params.modeFilter.length > 0 ? params.modeFilter : undefined;
      const msLevel =
        params.msLevelFilter.length > 0 ? params.msLevelFilter : undefined;

      const ids: string[] = [];
      let skip = 0;
      // Page through the matching samples until we've collected the full count.
      // `total` starts unbounded so the first page always runs; an empty page
      // breaks the loop as a guard against an unexpected runaway.
      let total = Infinity;
      while (ids.length < total) {
        const page = await novaApi.getPaginatedSamplesBySampleSet(
          params.sampleSetId,
          skip,
          FILTERED_SAMPLE_IDS_PAGE_SIZE,
          params.searchQuery || undefined,
          status,
          undefined,
          undefined,
          polarity,
          msLevel,
          params.metadataFilters,
          params.createdFrom ?? undefined,
          params.createdTo ?? undefined
        );
        if (page.data.length === 0) {
          break;
        }
        ids.push(...page.data.map((sample) => sample.id));
        total = page.total;
        skip += FILTERED_SAMPLE_IDS_PAGE_SIZE;
      }
      return { ids };
    },
  });

export const sampleSetMetadataValuesQueryOptions = (
  sampleSetId: string,
  hasPermission: (permission: PermissionString) => boolean
) =>
  queryOptions<{ values: Record<string, string[]> }>({
    queryKey: ['sampleSetMetadataValues', sampleSetId] as const,
    queryFn: async () => {
      const novaApi = new NovaApiClient(hasPermission);
      return await novaApi.getSampleSetMetadataValues(sampleSetId);
    },
    enabled: !!sampleSetId,
    staleTime: 5 * 60 * 1000,
  });

export const sampleSetMethodQueryOptions = (
  sampleSetId: string,
  hasPermission: (permission: PermissionString) => boolean
) =>
  queryOptions({
    queryKey: ['sampleSetMethod', sampleSetId] as const,
    queryFn: async () => {
      const novaApi = new NovaApiClient(hasPermission);
      return await novaApi.getSampleSetMethod(sampleSetId);
    },
    enabled: !!sampleSetId,
    refetchInterval: 120000, // Poll every 120 seconds
  });

export const sampleSetAcquisitionInfoQueryOptions = (
  sampleSetId: string,
  hasPermission: (permission: PermissionString) => boolean
) =>
  queryOptions({
    queryKey: ['sampleSetAcquisitionInfo', sampleSetId] as const,
    queryFn: async () => {
      const novaApi = new NovaApiClient(hasPermission);
      return await novaApi.getSampleSetAcquisitionInfo(sampleSetId);
    },
    enabled: !!sampleSetId,
  });

export const organizationUsersQueryOptions = (
  hasPermission: (permission: PermissionString) => boolean,
  organizationId: string | null,
  skip: number,
  limit: number,
  keyword: string
) =>
  queryOptions({
    queryKey: [
      'organizationUsers',
      organizationId,
      skip,
      limit,
      keyword,
    ] as const,
    queryFn: async (): Promise<PaginatedResponse<UserData>> => {
      const novaApi = new NovaApiClient(hasPermission);
      return await novaApi.getUsers(
        organizationId ?? undefined,
        skip,
        limit,
        keyword || undefined
      );
    },
    enabled: !!organizationId,
  });

export const organizationNamesQueryOptions = (
  hasPermission: (permission: PermissionString) => boolean,
  canReadOrganization: boolean
) =>
  queryOptions({
    queryKey: ['organizationNames'] as const,
    queryFn: async (): Promise<string[]> => {
      const novaApi = new NovaApiClient(hasPermission);
      return await novaApi.getOrganizationNames();
    },
    enabled: canReadOrganization,
  });

export const organizationByIdQueryOptions = (
  hasPermission: (permission: PermissionString) => boolean,
  organizationId: string | undefined,
  enabled: boolean
) =>
  queryOptions({
    queryKey: ['organization', organizationId] as const,
    queryFn: async (): Promise<Organization> => {
      const novaApi = new NovaApiClient(hasPermission);
      return await novaApi.getOrganizationById(organizationId!);
    },
    enabled: enabled && !!organizationId,
  });

export const productViewersQueryOptions = (
  hasPermission: (permission: PermissionString) => boolean
) =>
  queryOptions({
    queryKey: ['productViewers'] as const,
    queryFn: async (): Promise<ProductViewer[]> => {
      const novaApi = new NovaApiClient(hasPermission);
      return await novaApi.getProductViewers();
    },
  });

export const productsQueryOptions = (
  hasPermission: (permission: PermissionString) => boolean
) =>
  queryOptions({
    queryKey: ['products'] as const,
    queryFn: async (): Promise<Product[]> => {
      const novaApi = new NovaApiClient(hasPermission);
      return await novaApi.getProducts(false);
    },
  });

export const activeProductsQueryOptions = (
  hasPermission: (permission: PermissionString) => boolean
) =>
  queryOptions({
    queryKey: ['products', 'active'] as const,
    queryFn: async (): Promise<Product[]> => {
      const novaApi = new NovaApiClient(hasPermission);
      return await novaApi.getProducts(true);
    },
  });

export const configsQueryOptions = (
  hasPermission: (permission: PermissionString) => boolean
) =>
  queryOptions({
    queryKey: ['configs'] as const,
    queryFn: async (): Promise<Config[]> => {
      const novaApi = new NovaApiClient(hasPermission);
      return await novaApi.getConfigs();
    },
  });

export const modelsQueryOptions = (
  hasPermission: (permission: PermissionString) => boolean
) =>
  queryOptions({
    queryKey: ['models'] as const,
    queryFn: async (): Promise<Model[]> => {
      const novaApi = new NovaApiClient(hasPermission);
      return await novaApi.getModels();
    },
  });

export const agentJobStatusQueryOptions = (
  jobId: string,
  hasPermission: (permission: PermissionString) => boolean
) =>
  queryOptions<AgentJobStatus>({
    queryKey: ['agentJobStatus', jobId] as const,
    queryFn: async () => {
      const novaApi = new NovaApiClient(hasPermission);
      return await novaApi.getAgentJobStatus(jobId);
    },
    enabled: !!jobId,
  });

export const ms2SpectraQueryOptions = (
  jobId: string,
  sampleId: string,
  spectraId: string,
  hasPermission: (permission: PermissionString) => boolean
) =>
  queryOptions<SpectraResponse | null>({
    queryKey: ['ms2Spectra', jobId, sampleId, spectraId] as const,
    queryFn: async (): Promise<SpectraResponse | null> => {
      const novaApi = new NovaApiClient(hasPermission);
      const data: SpectraResponse[] = await novaApi.getMS2Spectra(
        jobId,
        sampleId,
        spectraId
      );
      return data?.[0] ?? null;
    },
    enabled: !!jobId && !!sampleId && !!spectraId,
  });
